/**
 * GMA-1616 (CompassCare Web 1/14 — Foundation) — the single design-token set.
 *
 * Every facelift screen (Overview, Vitals, Clients, Alerts, Map, Dispatch,
 * Devices, Reports, INTEL, Messages, Schedule, Audit Log, Settings) consumes
 * severity + theme tokens from HERE — there are intentionally NO per-page
 * color definitions. Adding a new severity requires editing this module and
 * SEVERITY_LEVELS together (the contract test in ./__tests__ enforces that).
 *
 * Dark command-center theme. Severity hexes are chosen for WCAG AA contrast on
 * the #0B0E14 background; reconcile exact shades against the design source
 * (CompassCare Wireframe Prototype.dc.html) as later screen stories land.
 */

// Dark command-center base surfaces + type. IBM Plex Sans for UI chrome, IBM
// Plex Mono for numerics/timestamps (vitals, KPIs, call durations).
export const theme = Object.freeze({
  bg: "#0A0D13",
  surface: "#10141D",
  border: "#1E2534",
  // Chrome text: primary (active/high-emphasis) + muted (inactive/secondary).
  // Sidebar, drawers, and tables consume these instead of hardcoding hexes.
  textPrimary: "#E8EEF5",
  textMuted: "#8FA1B3",
  fontUI: "'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', sans-serif",
  fontMono: "'IBM Plex Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace",
});

// Escalation order (low -> high), with Nora intelligence/informational last.
// Keep in lockstep with `severity` below.
export const SEVERITY_LEVELS = Object.freeze([
  "normal",
  "caution",
  "atRisk",
  "critical",
  "nora",
]);

// One badge/row/pin/KPI/drawer palette. `color` is the accent; `meaning` is the
// plain-language semantic the spec fixes; `label` is the human-facing chip text.
// Hexes reconciled to the hi-fi design source (CompassCare *.dc.html, 2026-07-08
// — see docs/design-mockups/). Contrast-checked for WCAG AA on the #0A0D13 bg.
export const severity = Object.freeze({
  normal: Object.freeze({ color: "#2ECC8F", label: "Normal", meaning: "normal" }),
  caution: Object.freeze({ color: "#F5C542", label: "Caution", meaning: "caution" }),
  atRisk: Object.freeze({ color: "#F5883D", label: "At risk", meaning: "at-risk" }),
  critical: Object.freeze({ color: "#F0524E", label: "Critical", meaning: "critical" }),
  nora: Object.freeze({ color: "#8B7CF6", label: "Nora", meaning: "informational" }),
});

/**
 * Resolve a severity token by level. Fails safe: an unknown, undefined, or null
 * level degrades to the benign `normal` token rather than throwing, so an
 * unexpected server value can never crash a screen.
 * @param {*} level ideally one of SEVERITY_LEVELS; any other value (unknown
 *   string, undefined, null, or non-string) resolves to the `normal` token.
 * @returns {{color:string,label:string,meaning:string}}
 */
export function severityToken(level) {
  return severity[level] || severity.normal;
}

const designTokens = Object.freeze({ theme, severity, SEVERITY_LEVELS });

export default designTokens;
